import React from 'react';
import { Box, Typography, Card, Skeleton, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  NavigateNextRounded,
  TrendingUpRounded,
  TrendingDownRounded,
  AutoAwesomeRounded,
} from '@mui/icons-material';
import { COLOR, GRADIENT, RADIUS, EASE, TONE_COLOR, TYPE, Tone, reducedMotionSx } from './tokens';

/**
 * STRATIFY PRIMITIVES — the shared building blocks every page composes from
 * (Design Constitution §33, §40, §41).
 *
 * The rule these exist to enforce: before inventing a pattern, check whether
 * one of these already solves it. A page that builds its own hero, its own
 * stat tile and its own empty state is how a product ends up looking like six
 * different products.
 *
 * Every primitive here takes surfaces and body text from the MUI theme, so the
 * dark preset keeps working, and takes identity from `./tokens`.
 */

// ───────────────────────────────────────────────────────────────────────────
// Decoration
// ───────────────────────────────────────────────────────────────────────────

/** Faint grid, used behind heroes to give a flat band some depth (§48.2).
 *  CSS rather than an asset: it tints from the theme and costs no request. */
export const GridMotif: React.FC<{ focus?: string }> = ({ focus = '88% 40%' }) => (
  <Box
    aria-hidden="true"
    sx={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      backgroundImage: (t) => `
        linear-gradient(${alpha(t.palette.text.primary, 0.05)} 1px, transparent 1px),
        linear-gradient(90deg, ${alpha(t.palette.text.primary, 0.05)} 1px, transparent 1px)`,
      backgroundSize: '44px 44px',
      maskImage: `radial-gradient(ellipse 80% 120% at ${focus}, #000 0%, transparent 70%)`,
      WebkitMaskImage: `radial-gradient(ellipse 80% 120% at ${focus}, #000 0%, transparent 70%)`,
    }}
  />
);

/** Headline text filled with the brand ramp. Use once per screen at most. */
export const RampText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      background: GRADIENT.ramp,
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
      WebkitTextFillColor: 'transparent',
    }}
  >
    {children}
  </Box>
);

/** Square tile behind an icon. `tone="ramp"` for emphasis, `soft` otherwise. */
export const IconTile: React.FC<{
  children: React.ReactNode;
  size?: number;
  tone?: 'ramp' | 'soft' | 'ai';
  color?: string;
}> = ({ children, size = 40, tone = 'soft', color }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      display: 'grid',
      placeItems: 'center',
      borderRadius: `${RADIUS.card}px`,
      transition: `background .22s ${EASE}`,
      ...reducedMotionSx,
      ...(tone === 'ramp'
        ? { background: GRADIENT.ramp, color: '#fff' }
        : tone === 'ai'
        ? { background: GRADIENT.ai, color: '#fff' }
        : {
            backgroundColor: alpha(color || COLOR.primary, 0.1),
            color: color || COLOR.primary,
          }),
      '& .MuiSvgIcon-root': { fontSize: size * 0.5 },
    }}
  >
    {children}
  </Box>
);

// ───────────────────────────────────────────────────────────────────────────
// Page header (§13)
// ───────────────────────────────────────────────────────────────────────────

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * The standard page hero: breadcrumb → title → description → actions, with an
 * optional right-hand lockup for the band's empty corner.
 *
 * Every authenticated page uses this. A module that hand-rolls its own header
 * breaks §13 and is immediately visible when two screens sit side by side.
 */
export const PageHero: React.FC<{
  crumbs?: Crumb[];
  title: string;
  description?: string;
  /** Right-aligned two-line statement. Optional — omit on dense list pages. */
  lockup?: { line1: string; line2: string };
  actions?: React.ReactNode;
  /** Slot under the description (perspective / focus / date controls, §13). */
  context?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ crumbs, title, description, lockup, actions, context, children }) => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: `${RADIUS.container}px`,
      mb: 3,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      backgroundImage: (t) =>
        `radial-gradient(ellipse 90% 140% at 82% -20%, ${alpha(COLOR.aiLight, 0.14)} 0%, transparent 62%),
         radial-gradient(ellipse 70% 120% at 8% 0%, ${alpha(COLOR.indigoLight, 0.11)} 0%, transparent 58%),
         linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.03)} 0%, transparent 100%)`,
    }}
  >
    <GridMotif />
    <Box
      sx={{
        position: 'relative',
        px: { xs: 2.5, md: 4 },
        pt: { xs: 2.5, md: 3.5 },
        pb: { xs: 2.5, md: 3 },
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 3,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0, maxWidth: 760 }}>
        {crumbs && crumbs.length > 0 && (
          <Box
            component="nav"
            aria-label="Breadcrumb"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.25, flexWrap: 'wrap' }}
          >
            {crumbs.map((c, i) => (
              <React.Fragment key={`${c.label}-${i}`}>
                {i > 0 && (
                  <NavigateNextRounded sx={{ fontSize: 15, color: 'text.disabled' }} aria-hidden="true" />
                )}
                {c.to ? (
                  <Box
                    component={RouterLink}
                    to={c.to}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                    }}
                  >
                    {c.label}
                  </Box>
                ) : (
                  <Typography
                    component="span"
                    aria-current="page"
                    sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}
                  >
                    {c.label}
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}

        <Typography component="h1" sx={{ ...TYPE.pageTitle, fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: description ? 1 : 0 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, maxWidth: 680 }}>
            {description}
          </Typography>
        )}
        {context && <Box sx={{ mt: 1.75 }}>{context}</Box>}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
        {lockup && (
          <Box sx={{ display: { xs: 'none', lg: 'block' }, textAlign: 'right', mr: 1 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
              {lockup.line1}
            </Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
              <RampText>{lockup.line2}</RampText>
            </Typography>
          </Box>
        )}
        {actions}
      </Box>
    </Box>
    {children}
  </Box>
);

/** Section heading with a gradient mark. Used above each band of a page. */
export const SectionHeading: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}> = ({ icon, title, subtitle, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
    <IconTile size={34} tone="ramp">
      {icon}
    </IconTile>
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography sx={TYPE.sectionTitle}>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

// ───────────────────────────────────────────────────────────────────────────
// Metrics (§48.4 — a number must carry value + direction + context)
// ───────────────────────────────────────────────────────────────────────────

export interface MetricTileProps {
  label: string;
  /** null renders the skeleton — every component supports loading (§37). */
  value: React.ReactNode | null;
  icon?: React.ReactNode;
  tone?: Tone;
  /** Signed percentage change. Omit when there is no honest comparison. */
  deltaPct?: number | null;
  /** What the delta is measured against, e.g. "vs last week". */
  deltaLabel?: string;
  /** True when a RISE is bad (cost, ageing, rejections). */
  invertDelta?: boolean;
  hint?: string;
  onClick?: () => void;
}

/**
 * A single business number.
 *
 * Renders the delta only when one is actually supplied — an ERP must never
 * invent a trend it cannot compute, and a fake "↑ 12%" is worse than no
 * comparison at all.
 */
export const MetricTile: React.FC<MetricTileProps> = ({
  label,
  value,
  icon,
  tone = 'info',
  deltaPct,
  deltaLabel,
  invertDelta,
  hint,
  onClick,
}) => {
  const accent = TONE_COLOR[tone];
  const hasDelta = deltaPct !== null && deltaPct !== undefined && Number.isFinite(deltaPct);
  const up = hasDelta && (deltaPct as number) > 0;
  const flat = hasDelta && (deltaPct as number) === 0;
  // Good/bad is a business question, not a direction question: ageing going up
  // is bad, approvals going up is good.
  const good = flat ? null : invertDelta ? !up : up;

  return (
    <Card
      elevation={0}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        p: 2.25,
        borderRadius: `${RADIUS.card}px`,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        cursor: onClick ? 'pointer' : 'default',
        transition: `border-color .2s ${EASE}, transform .2s ${EASE}`,
        ...reducedMotionSx,
        ...(onClick ? { '&:hover': { transform: 'translateY(-2px)', borderColor: accent } } : {}),
        '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
      }}
    >
      {/* Tone wash — carries the semantic colour without tinting the number. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(135deg, ${alpha(accent, 0.07)} 0%, transparent 55%)`,
        }}
      />
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {icon && <IconTile size={40} color={accent}>{icon}</IconTile>}
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          {value === null ? (
            <Skeleton width={90} height={34} />
          ) : (
            <Typography sx={{ ...TYPE.metric, fontSize: '1.5rem' }}>{value}</Typography>
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}
          >
            {label}
          </Typography>
          {(hasDelta || hint) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
              {hasDelta && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.625,
                    py: 0.125,
                    borderRadius: `${RADIUS.pill}px`,
                    fontSize: '0.6875rem',
                    fontWeight: 800,
                    color: good === null ? COLOR.inkMuted : good ? COLOR.success : COLOR.danger,
                    backgroundColor: alpha(
                      good === null ? COLOR.inkMuted : good ? COLOR.success : COLOR.danger,
                      0.12
                    ),
                  }}
                >
                  {!flat &&
                    (up ? (
                      <TrendingUpRounded sx={{ fontSize: 13 }} />
                    ) : (
                      <TrendingDownRounded sx={{ fontSize: 13 }} />
                    ))}
                  {Math.abs(deltaPct as number).toFixed(0)}%
                </Box>
              )}
              {(deltaLabel || hint) && (
                <Typography variant="caption" color="text.secondary">
                  {deltaLabel || hint}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// AI surface (§21, §48.9) — violet, luminous, never a cartoon robot
// ───────────────────────────────────────────────────────────────────────────

/** Small "this is AI" marker. Put it on any Stratify Intelligence surface. */
export const AiBadge: React.FC<{ label?: string }> = ({ label = 'Stratify Intelligence' }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.625,
      px: 1,
      py: 0.375,
      borderRadius: `${RADIUS.pill}px`,
      fontSize: '0.6875rem',
      fontWeight: 800,
      letterSpacing: '0.01em',
      color: COLOR.ai,
      backgroundColor: alpha(COLOR.ai, 0.1),
      border: `1px solid ${alpha(COLOR.ai, 0.24)}`,
    }}
  >
    <AutoAwesomeRounded sx={{ fontSize: 13 }} />
    {label}
  </Box>
);

/**
 * The dark AI panel.
 *
 * Deliberately a `button` when `onOpen` is given: the panel is an entry point
 * into the real assistant, not a decorative block. Callers must gate rendering
 * on the assistant actually being configured — an AI surface that errors on
 * click is worse than no AI surface.
 */
export const AiPanel: React.FC<{
  title?: string;
  body: string;
  cta?: string;
  onOpen?: () => void;
  children?: React.ReactNode;
}> = ({ title = 'Stratify AI', body, cta, onOpen, children }) => (
  <Card
    elevation={0}
    role={onOpen ? 'button' : undefined}
    tabIndex={onOpen ? 0 : undefined}
    onClick={onOpen}
    onKeyDown={
      onOpen
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen();
            }
          }
        : undefined
    }
    sx={{
      position: 'relative',
      overflow: 'hidden',
      p: 2.25,
      borderRadius: `${RADIUS.container}px`,
      background: GRADIENT.night,
      color: '#fff',
      cursor: onOpen ? 'pointer' : 'default',
      transition: `transform .22s ${EASE}`,
      ...reducedMotionSx,
      ...(onOpen ? { '&:hover': { transform: 'translateY(-2px)' } } : {}),
      '&:focus-visible': { outline: `2px solid ${COLOR.aiLight}`, outlineOffset: 2 },
    }}
  >
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `radial-gradient(circle at 85% 0%, ${alpha(COLOR.aiLight, 0.45)} 0%, transparent 55%)`,
      }}
    />
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <AutoAwesomeRounded sx={{ fontSize: 18, color: COLOR.aiGlow }} />
        <Typography sx={{ fontWeight: 800, fontSize: '0.9375rem', letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.76)', lineHeight: 1.55, mb: cta || children ? 1.5 : 0 }}>
        {body}
      </Typography>
      {children}
      {cta && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: `${RADIUS.input}px`,
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.18)',
          }}
        >
          <Typography variant="body2" sx={{ flexGrow: 1, color: 'rgba(255,255,255,0.68)' }} noWrap>
            {cta}
          </Typography>
          <Box
            aria-hidden="true"
            sx={{
              width: 26,
              height: 26,
              display: 'grid',
              placeItems: 'center',
              borderRadius: `${RADIUS.button}px`,
              background: GRADIENT.ramp,
              flexShrink: 0,
            }}
          >
            <NavigateNextRounded sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      )}
    </Box>
  </Card>
);

// ───────────────────────────────────────────────────────────────────────────
// States (§37, §48.10, §48.11)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Premium empty state. Never "No data found" — say what the surface is for and
 * offer the action that fills it.
 */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  body?: string;
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
}> = ({ icon, title, body, primaryAction, secondaryAction }) => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      py: 7,
      px: 3,
      textAlign: 'center',
      borderRadius: `${RADIUS.container}px`,
      border: '1px dashed',
      borderColor: 'divider',
      backgroundColor: (t) => alpha(t.palette.text.primary, 0.015),
    }}
  >
    <GridMotif focus="50% 0%" />
    <Box sx={{ position: 'relative' }}>
      {icon && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <IconTile size={48} tone="soft">
            {icon}
          </IconTile>
        </Box>
      )}
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>{title}</Typography>
      {body && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
          {body}
        </Typography>
      )}
      {(primaryAction || secondaryAction) && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2.5, flexWrap: 'wrap' }}>
          {primaryAction && (
            <Button
              variant="contained"
              disableElevation
              onClick={primaryAction.onClick}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS.button}px`, background: GRADIENT.ramp }}
            >
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outlined"
              onClick={secondaryAction.onClick}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS.button}px` }}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Box>
      )}
    </Box>
  </Box>
);

/**
 * Contextual loading. A spinner says "wait"; this says what is being waited on,
 * which is the difference between a stall and a system doing work (§48.11).
 */
export const LoadingState: React.FC<{ message?: string; rows?: number }> = ({
  message = 'Loading…',
  rows = 4,
}) => (
  <Box sx={{ p: 2.5 }} role="status" aria-live="polite">
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
      <AutoAwesomeRounded sx={{ fontSize: 17, color: COLOR.ai }} />
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {message}
      </Typography>
    </Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton
        key={i}
        height={44}
        sx={{ borderRadius: `${RADIUS.input}px`, mb: 1, transform: 'none', opacity: 1 - i * 0.14 }}
      />
    ))}
  </Box>
);
